import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { BaseApiService, ApiResponse, PaginationInfo } from "./baseApi";
import { Customer } from "./customerApi";
import { Endgood } from "./endgoodApi";
import { EndgoodCategory } from "./endgoodCategoryApi";

// TODO: IMPLEMENTATION NOTE
// Currently using hardcoded values for all sales order API calls:
// - branchId = 1 for all operations (listing, create, update, delete, detail)
// - createdBy = 1 for create operations
// This is a temporary solution to make the API work while implementing proper branch selection
// To implement proper branch selection:
// 1. Remove hardcoded branchId = 1 from all methods
// 2. Remove hardcoded createdBy = 1 from create method
// 3. Use the branch context to get the selected branch
// 4. Use user context to get the current user ID for createdBy/updatedBy
// 5. Pass the selected branch ID and user ID to all API calls
// 6. Handle cases where no branch is selected

// Branch interface for sales order API (matching Swagger specification)
export interface SalesOrderBranch {
  id: number;
  branchName: string;
  branchCode: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  isActive: boolean;
  gstin: string;
  stateName: string;
  stateCode: string;
  panNo: string;
  createdAt: string;
  updatedAt: string;
}

// Sales Order Detail (Line Item) interfaces
export interface SalesOrderDetail {
  id: number;
  quantity: number;
  unitPrice: number;
  discountPercentage: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  lineTotal: number;
  salesOrderId: number;
  endgoodId: number;
  endgoodCategoryId: number;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  salesOrder?: SalesOrder;
  endgood?: Endgood;
  endgoodCategory?: EndgoodCategory;
}

export interface SalesOrderDetailCreateRequest {
  quantity: number;
  unitPrice: number;
  discountPercentage: number;
  discountAmount: number;
  taxPercentage: number;
  taxAmount: number;
  lineTotal: number;
  salesOrderId: number;
  endgoodId: number;
  endgoodCategoryId: number;
  createdBy?: number;
  updatedBy?: number;
}

export interface SalesOrderDetailUpdateRequest extends SalesOrderDetailCreateRequest {
  // Same structure as create request
  id: number;
}

// Batch request is just an array - can include both new items (without id) and existing items (with id)
export type SalesOrderDetailBatchRequest = Array<SalesOrderDetailCreateRequest | SalesOrderDetailUpdateRequest>;

// Sales Order interface
export interface SalesOrder {
  id: number;
  soNumber: string;
  orderDate: string;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  notes: string;
  refNo: string;
  refDate: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  customerId: number;
  customerName: string;
  customerCode: string;
  branchId: number;
  branchName: string;
  branchCode: string;
  itemCount?: number; // Number of items in the sales order
  details?: SalesOrderDetail[]; // Optional line items/details
}

// Sales Order data for create operations
export interface SalesOrderData {
  soNumber?: string; // Optional - will be generated by server
  orderDate: string;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  notes: string;
  refNo: string;
  refDate: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  customerId: number;
  branchId: number;
}

// Sales Order data for update operations (includes all fields)
export interface SalesOrderUpdateData {
  id: number;
  soNumber: string;
  orderDate: string;
  status: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  notes: string;
  refNo: string;
  refDate: string;
  totalAmount: number;
  taxAmount: number;
  grandTotal: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  customer: Customer;
  branch: SalesOrderBranch;
}





export class SalesOrderApiService extends BaseApiService {
  // Helper method to format dates consistently
  private formatDate(dateString: string): string {
    if (!dateString) return '';
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  }


  // Get all sales orders with pagination
  async getSalesOrders(page: number = 1, pageSize: number = 10, branchId?: number): Promise<ApiResponse<SalesOrder[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      const finalBranchId = branchId || 1;
      params.append('branchId', finalBranchId.toString());

      const response = await this.makeRequest<ApiResponse<SalesOrder[]>>(`${API_ENDPOINTS.SALES_ORDERS.LIST}?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to fetch sales orders');
      throw error;
    }
  }

  // Get sales orders by status (e.g. OPEN for invoice create)
  async getSalesOrdersByStatus(status: string, branchId?: number): Promise<ApiResponse<SalesOrder[]>> {
    try {
      const params = new URLSearchParams();
      if (branchId != null) {
        params.append('branchId', branchId.toString());
      }
      const query = params.toString();
      const url = query
        ? `${API_ENDPOINTS.SALES_ORDERS.BY_STATUS(status)}?${query}`
        : API_ENDPOINTS.SALES_ORDERS.BY_STATUS(status);
      const response = await this.makeRequest<ApiResponse<SalesOrder[]>>(url);
      return response;
    } catch (error) {
      toast.error('Failed to fetch sales orders by status');
      throw error;
    }
  }

  // Get sales order by ID
  async getSalesOrderById(id: string): Promise<ApiResponse<SalesOrder>> {
    try {
      // console.log(`🕐 Starting sales order fetch for ID: ${id}`);
      // TODO: Replace hardcoded branchId with actual branch selection from context
      // For now, using hardcoded branchId = 1 to make API calls work
      const params = new URLSearchParams({
        branchId: '1' // Hardcoded branchId = 1
      });

      const response = await this.makeRequest<ApiResponse<SalesOrder>>(
        `${API_ENDPOINTS.SALES_ORDERS.DETAIL(id)}?${params.toString()}`
      );
      // console.log(`✅ Sales order fetch completed for ID: ${id}`);
      return response;
    } catch (error) {
      // console.error(`❌ Sales order fetch failed for ID: ${id}`, error);
      toast.error('Failed to fetch sales order details');
      throw error;
    }
  }

  // Create sales order
  async createSalesOrder(salesOrderData: Omit<SalesOrderData, 'customerId' | 'branchId'>, customerId: number, branchId: number): Promise<ApiResponse<SalesOrder>> {
    try {
      // TODO: Replace hardcoded values with actual user context and branch selection
      // For now, using hardcoded values to make API calls work
      const params = new URLSearchParams({
        branchId: branchId.toString(),
        createdBy: '1'  // Hardcoded createdBy = 1
      });

              // Construct the full payload with nested customer and branch objects
        const fullPayload: SalesOrderData = {
          // soNumber will be generated by server, so we don't include it
          orderDate: this.formatDate(salesOrderData.orderDate),
          status: salesOrderData.status,
          effectiveStartDate: this.formatDate(salesOrderData.effectiveStartDate),
          effectiveEndDate: this.formatDate(salesOrderData.effectiveEndDate),
          notes: salesOrderData.notes,
          refNo: salesOrderData.refNo,
          refDate: this.formatDate(salesOrderData.refDate),
          totalAmount: salesOrderData.totalAmount || 0,
          taxAmount: salesOrderData.taxAmount || 0,
          grandTotal: salesOrderData.grandTotal || 0,
          isActive: salesOrderData.isActive,
          customerId: customerId,
          branchId: branchId,
        };

      const response = await this.makeRequest<ApiResponse<SalesOrder>>(`${API_ENDPOINTS.SALES_ORDERS.CREATE}?${params.toString()}`, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(fullPayload),
      });
      toast.success('Sales order created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create sales order');
      throw error;
    }
  }

  // Update sales order
  async updateSalesOrder(id: string, salesOrderData: Omit<SalesOrderData, 'customerId' | 'branchId'>, customerId: number, branchId: number, existingOrder?: SalesOrder): Promise<ApiResponse<SalesOrder>> {
    try {
      // TODO: Replace hardcoded values with actual user context and branch selection
      // For now, using hardcoded values to make API calls work
            // Construct a simplified update payload with just the essential fields
      const updatePayload = {
        id: parseInt(id),
        soNumber: salesOrderData.soNumber || '',
        orderDate: this.formatDate(salesOrderData.orderDate),
        status: salesOrderData.status,
        effectiveStartDate: this.formatDate(salesOrderData.effectiveStartDate),
        effectiveEndDate: this.formatDate(salesOrderData.effectiveEndDate),
        notes: salesOrderData.notes,
        refNo: salesOrderData.refNo,
        refDate: this.formatDate(salesOrderData.refDate),
        totalAmount: salesOrderData.totalAmount || 0,
        taxAmount: salesOrderData.taxAmount || 0,
        grandTotal: salesOrderData.grandTotal || 0,
        isActive: salesOrderData.isActive,
        customerId: customerId,
        branchId: branchId,
        updatedBy: 1
      };

      const response = await this.makeRequest<ApiResponse<SalesOrder>>(`${API_ENDPOINTS.SALES_ORDERS.UPDATE(id)}`, {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(updatePayload),
      });
      toast.success('Sales order updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update sales order');
      throw error;
    }
  }

  // Delete sales order
  async deleteSalesOrder(id: string): Promise<ApiResponse<void>> {
    try {
      // TODO: Replace hardcoded branchId with actual branch selection from context
      // For now, using hardcoded branchId = 1 to make API calls work
      const params = new URLSearchParams({
        branchId: '1' // Hardcoded branchId = 1
      });

      const response = await this.makeRequest<ApiResponse<void>>(`${API_ENDPOINTS.SALES_ORDERS.DELETE(id)}?${params.toString()}`, {
        method: HTTP_METHODS.DELETE,
      });
      toast.success('Sales order deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete sales order');
      throw error;
    }
  }

  // Order Detail (Line Item) methods
  async getSalesOrderDetails(salesOrderId: string): Promise<ApiResponse<SalesOrderDetail[]>> {
    try {
      // console.log(`🕐 Starting sales order details fetch for ID: ${salesOrderId}`);
      const response = await this.makeRequestWithTimeout<ApiResponse<SalesOrderDetail[]>>(
        API_ENDPOINTS.SALES_ORDERS.ITEMS(salesOrderId),
        {},
        30000 // 30 second timeout
      );
      // console.log(`✅ Sales order details fetch completed for ID: ${salesOrderId}`);
      return response;
    } catch (error) {
      // console.error(`❌ Sales order details fetch failed for ID: ${salesOrderId}`, error);
      toast.error('Failed to fetch sales order details');
      throw error;
    }
  }

  async createSalesOrderDetail(detailData: any): Promise<ApiResponse<SalesOrderDetail>> {
    try {
      // Transform the data to match the expected API structure
      const now = new Date().toISOString().split('T')[0]; // Format as yyyy-MM-dd


      
      const rawEndgoodId = detailData.endgoodId ?? detailData.endGood;
      const endgoodId = rawEndgoodId != null ? parseInt(String(rawEndgoodId), 10) : NaN;
      if (isNaN(endgoodId) || endgoodId === 0) {
        toast.error('Order detail requires a valid endgoodId');
        throw new Error('Order detail requires a valid endgoodId');
      }
      const rawCategoryId = detailData.endgoodCategoryId ?? detailData.endgoodCategory ?? detailData.categoryId;
      const categoryIdNum = rawCategoryId != null ? Number(rawCategoryId) : NaN;
      const hasCategoryId = !isNaN(categoryIdNum) && categoryIdNum !== 0;
      const transformedData: SalesOrderDetailCreateRequest & { categoryId?: number } = {
        quantity: detailData.quantity,
        unitPrice: detailData.unitPrice,
        discountPercentage: detailData.discountPercentage || detailData.discount || 0,
        discountAmount: detailData.discountAmount || 0,
        taxPercentage: detailData.taxPercentage || detailData.taxRate || 0,
        taxAmount: detailData.taxAmount || 0,
        lineTotal: detailData.lineTotal || detailData.total || 0,
        createdBy: detailData.createdBy || 1,
        updatedBy: detailData.updatedBy || 1,
        salesOrderId: parseInt(detailData.salesOrder || detailData.salesOrderId || 0),
        endgoodId,
        endgoodCategoryId: hasCategoryId ? categoryIdNum : (0 as number), // API may require field; only send real value when we have it
      };
      if (hasCategoryId) {
        transformedData.categoryId = categoryIdNum;
      }

      const response = await this.makeRequest<ApiResponse<SalesOrderDetail>>(
        API_ENDPOINTS.SALES_ORDERS.CREATE_ITEM(transformedData.salesOrderId.toString()),
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(transformedData),
        }
      );
      toast.success('Order detail created successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to create order detail');
      throw error;
    }
  }

  async updateSalesOrderDetail(id: string, detailData: SalesOrderDetailUpdateRequest): Promise<ApiResponse<SalesOrderDetail>> {
    try {
      const response = await this.makeRequest<ApiResponse<SalesOrderDetail>>(
        API_ENDPOINTS.SALES_ORDERS.UPDATE_ITEM(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(detailData),
        }
      );
      toast.success('Order detail updated successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to update order detail');
      throw error;
    }
  }

  async deleteSalesOrderDetail(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.SALES_ORDERS.DELETE_ITEM(id),
        {
          method: HTTP_METHODS.DELETE,
        }
      );
      toast.success('Order detail deleted successfully!');
      return response;
    } catch (error) {
      toast.error('Failed to delete order detail');
      throw error;
    }
  }

  async batchCreateSalesOrderDetails(orderId: string, batchData: SalesOrderDetailBatchRequest): Promise<ApiResponse<SalesOrderDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<SalesOrderDetail[]>>(
        API_ENDPOINTS.SALES_ORDERS.BATCH_CREATE_DETAILS(orderId),
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(batchData),
        }
      );
      if (response.success) {
        toast.success(`Successfully created ${batchData.length} line items`);
      }
      return response;
    } catch (error) {
      toast.error('Failed to batch create order details');
      throw error;
    }
  }

  async batchUpdateSalesOrderDetails(orderId: string, batchData: SalesOrderDetailBatchRequest): Promise<ApiResponse<SalesOrderDetail[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<SalesOrderDetail[]>>(
        API_ENDPOINTS.SALES_ORDERS.BATCH_UPDATE_DETAILS(orderId),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(batchData),
        }
      );
      if (response.success) {
        toast.success(`Successfully updated ${batchData.length} line items`);
      }
      return response;
    } catch (error) {
      toast.error('Failed to batch update order details');
      throw error;
    }
  }

  // Search sales orders
  async searchSalesOrders(query: string, page: number = 1, pageSize: number = 10, branchId?: number): Promise<ApiResponse<SalesOrder[]>> {
    try {
      const params = new URLSearchParams({
        query,
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // TODO: Replace hardcoded branchId with actual branch selection from context
      // For now, using branchId = 1 to make API calls work
      const finalBranchId = branchId || 1;
      params.append('branchId', finalBranchId.toString());

      const response = await this.makeRequest<ApiResponse<SalesOrder[]>>(`${API_ENDPOINTS.SALES_ORDERS.SEARCH}?${params.toString()}`);
      return response;
    } catch (error) {
      toast.error('Failed to search sales orders');
      throw error;
    }
  }

  // Helper method to extract data from ApiResponse
  extractData<T>(response: ApiResponse<T>): T {
    return response.data;
  }

  // Helper method to get sales orders with pagination data
  async getSalesOrdersData(page: number = 1, pageSize: number = 10, branchId?: number): Promise<{ salesOrders: SalesOrder[], pagination: PaginationInfo }> {
    const response = await this.getSalesOrders(page, pageSize, branchId);
    const salesOrders = this.extractData(response);
    
    // Handle optional pagination
    const pagination = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: salesOrders.length,
      pageSize: pageSize
    };

    return { salesOrders, pagination };
  }

  // Helper method to search sales orders with pagination data
  async searchSalesOrdersData(query: string, page: number = 1, pageSize: number = 10, branchId?: number): Promise<{ salesOrders: SalesOrder[], pagination: PaginationInfo }> {
    const response = await this.searchSalesOrders(query, page, pageSize, branchId);
    const salesOrders = this.extractData(response);
    
    // Handle optional pagination
    const pagination = response.pagination || {
      currentPage: page,
      totalPages: 1,
      totalCount: salesOrders.length,
      pageSize: pageSize
    };

    return { salesOrders, pagination };
  }
}

export const salesOrderApiService = new SalesOrderApiService();
export default salesOrderApiService;
